import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import Image from '@vs-shop/image-link/Image';
import type { VsImageProps } from '@vs-shop/headless/useVsImage';
import { clsxm, type ClassValue } from '@vs-shop/utils';

/**
 * What a search panel actually lists: rows of words, and rows of products.
 *
 *   <SearchComboboxGroup>
 *     <SearchComboboxGroupLabel>{heading}</SearchComboboxGroupLabel>
 *     <SearchSuggestLinkList>
 *       <SearchSuggestRow>
 *         <SearchComboboxItem value={hit} render={<SearchSuggestLink href={url} />}>
 *           {label}
 *         </SearchComboboxItem>
 *       </SearchSuggestRow>
 *     </SearchSuggestLinkList>
 *   </SearchComboboxGroup>
 *
 * **A suggestion is an option before it is a link**, so the element that
 * carries the keyboard behaviour is `@vs-shop/ui`'s `SearchComboboxItem` and
 * these parts paint through it: the item contributes the value and the
 * highlight state, `SearchSuggestLink` contributes the look and the anchor.
 * Nothing about a hit — its id, its url, its price, what it matched — crosses
 * into this package.
 *
 * **The grid row and the featured row are one part with two appearances.** They
 * differ in which way the crop and the copy stack and how large each reads,
 * which is the same box painting itself differently — not a different anatomy.
 * What differs structurally is only that a grid row wraps its copy in a body
 * that pushes the price to the bottom, and a composition either writes that or
 * does not.
 */

/** The list a group of word suggestions is: tight, because each row is one line. */
const searchSuggestLinkListClasses = 'flex flex-col gap-1';

export type SearchSuggestLinkListProps = ComponentProps<'ul'>;

/** A group of word suggestions — brands, categories, articles, phrases. */
export const SearchSuggestLinkList = ({ className, ...props }: SearchSuggestLinkListProps) => (
  <ul
    data-slot="search-suggest-link-list"
    className={clsxm(searchSuggestLinkListClasses, className)}
    {...props}
  />
);

/**
 * One cell of a suggestion list.
 *
 * `min-w-0` is the whole part: without it a long product name sets the cell's
 * minimum width, and a grid of them pushes the panel wider than the popup it
 * sits in. Invisible until a shop ships a long name.
 */
const searchSuggestRowClasses = 'min-w-0';

export type SearchSuggestRowProps = ComponentProps<'li'>;

/** One cell of a suggestion list. */
export const SearchSuggestRow = ({ className, ...props }: SearchSuggestRowProps) => (
  <li
    data-slot="search-suggest-row"
    className={clsxm(searchSuggestRowClasses, className)}
    {...props}
  />
);

/**
 * A word suggestion. Truncates rather than wraps — the rows are a scan, and a
 * phrase that took two lines would break the rhythm of the column.
 *
 * Highlighted and hovered read the same, so a pointer and the arrow keys agree
 * about which row is the current one.
 */
const searchSuggestLinkClasses =
  'text-foreground-muted hover:text-foreground data-[highlighted]:text-foreground block truncate text-sm hover:underline data-[highlighted]:underline';

export type SearchSuggestLinkProps = ComponentProps<'a'>;

/** A word suggestion. */
export const SearchSuggestLink = ({ className, ...props }: SearchSuggestLinkProps) => (
  <a
    data-slot="search-suggest-link"
    className={clsxm(searchSuggestLinkClasses, className)}
    {...props}
  />
);

/** The grid the product suggestions sit in: one column narrow, two from `tab`. */
export const searchSuggestProductGridClasses = 'tab:grid-cols-2 grid grid-cols-1 gap-3';

export type SearchSuggestProductGridProps = ComponentProps<'ul'>;

/** The grid of product suggestions. */
export const SearchSuggestProductGrid = ({
  className,
  ...props
}: SearchSuggestProductGridProps) => (
  <ul
    data-slot="search-suggest-product-grid"
    className={clsxm(searchSuggestProductGridClasses, className)}
    {...props}
  />
);

/** A product suggestion's click surface. */
export const searchSuggestProductLinkVariants = cva(
  'hover:bg-background-intense data-[highlighted]:bg-background-intense rounded-md p-2',
  {
    variants: {
      variant: {
        /** Crop beside copy, filling the cell so a grid row keeps its neighbours level. */
        row: 'flex h-full gap-3',
        /** Crop above copy, in the column it has to itself. */
        feature: 'flex flex-col gap-2',
      },
    },
  }
);

type SearchSuggestVariantName = NonNullable<
  VariantProps<typeof searchSuggestProductLinkVariants>['variant']
>;

export type SearchSuggestVariant = SearchSuggestVariantName;

/**
 * `satisfies` binds these to the recipes above and below, so the union and the
 * arms cannot drift.
 */
export const SearchSuggestVariant = {
  /** One of several, in a grid. */
  ROW: 'row',
  /** The single most relevant one. */
  FEATURE: 'feature',
} as const satisfies Record<string, SearchSuggestVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type SearchSuggestVariantProps = { readonly variant: SearchSuggestVariant };

export type SearchSuggestProductLinkProps = ComponentProps<'a'> & SearchSuggestVariantProps;

/** A product suggestion's click surface. */
export const SearchSuggestProductLink = ({
  className,
  variant,
  ...props
}: SearchSuggestProductLinkProps) => (
  <a
    data-slot="search-suggest-product-link"
    className={clsxm(searchSuggestProductLinkVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The crop.
 *
 * Contained rather than cropped: a suggestion is a subject on a ground, and a
 * cover crop would cut the thing being suggested. `src` is final and
 * renderable — filter and CDN resolution stays app-side (spec § "Media parts")
 * — and there is no `sources`, because a row in a popup is not art-directed.
 */
export const searchSuggestProductImageVariants = cva(
  'bg-background aspect-[4/3] rounded-md object-contain',
  {
    variants: {
      variant: {
        /** A fixed narrow crop that does not shrink, held to the top of its row. */
        row: 'w-20 shrink-0 self-start',
        /** As wide as the column allows, up to the crop's own size. */
        feature: 'w-full max-w-[215px]',
      },
    },
  }
);

export type SearchSuggestProductImageProps = Omit<VsImageProps, 'className' | 'fill'> &
  SearchSuggestVariantProps & {
    readonly className?: ClassValue;
  };

/** A product suggestion's crop. */
export const SearchSuggestProductImage = ({
  className,
  variant,
  ...props
}: SearchSuggestProductImageProps) => (
  <Image className={clsxm(searchSuggestProductImageVariants({ variant }), className)} {...props} />
);

/**
 * The same box with no crop in it — for a product whose imagery is missing
 * before anything is requested, which is a different thing from a URL that
 * failed. What stands in it is the tenant's mark.
 */
export const searchSuggestProductImagePlaceholderVariants = cva(
  'bg-background-intense flex aspect-[4/3] items-center justify-center rounded-md',
  {
    variants: {
      variant: {
        /** The row's fixed narrow box. */
        row: 'w-20 shrink-0 self-start',
        /** The feature's full-width box. */
        feature: 'w-full max-w-[215px]',
      },
    },
  }
);

export type SearchSuggestProductImagePlaceholderProps = ComponentProps<'div'> &
  SearchSuggestVariantProps;

/** The crop's box, with nothing in it. */
export const SearchSuggestProductImagePlaceholder = ({
  className,
  variant,
  ...props
}: SearchSuggestProductImagePlaceholderProps) => (
  <div
    data-slot="search-suggest-product-image-placeholder"
    className={clsxm(searchSuggestProductImagePlaceholderVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The copy column of a grid row: fills the row's height and pushes the price
 * against its bottom, so prices align across a row of cells whose names ran to
 * different numbers of lines.
 *
 * The featured suggestion has no such column — its crop, name and price are the
 * link's own children — which is why this is a part a composition writes rather
 * than an appearance of one.
 */
const searchSuggestProductBodyClasses = 'flex min-w-0 flex-1 flex-col justify-between gap-1';

export type SearchSuggestProductBodyProps = ComponentProps<'div'>;

/** The copy column of a grid row. */
export const SearchSuggestProductBody = ({
  className,
  ...props
}: SearchSuggestProductBodyProps) => (
  <div
    data-slot="search-suggest-product-body"
    className={clsxm(searchSuggestProductBodyClasses, className)}
    {...props}
  />
);

/** A product suggestion's name. Clamped rather than truncated: a name is worth lines. */
export const searchSuggestProductLabelVariants = cva('text-foreground font-medium', {
  variants: {
    variant: {
      /** Three lines in a grid cell. */
      row: 'line-clamp-3 text-xs',
      /** Two larger ones in the column it has to itself. */
      feature: 'line-clamp-2 text-sm',
    },
  },
});

export type SearchSuggestProductLabelProps = ComponentProps<'span'> & SearchSuggestVariantProps;

/** A product suggestion's name. */
export const SearchSuggestProductLabel = ({
  className,
  variant,
  ...props
}: SearchSuggestProductLabelProps) => (
  <span
    data-slot="search-suggest-product-label"
    className={clsxm(searchSuggestProductLabelVariants({ variant }), className)}
    {...props}
  />
);

/** A product suggestion's price, already formatted by whoever knows the currency. */
export const searchSuggestProductPriceVariants = cva('font-heading text-foreground font-semibold', {
  variants: {
    variant: {
      /** At the foot of a grid cell. */
      row: 'text-xs',
      /** Under the featured name, where there is room to read it. */
      feature: 'text-base',
    },
  },
});

export type SearchSuggestProductPriceProps = ComponentProps<'span'> & SearchSuggestVariantProps;

/** A product suggestion's price. */
export const SearchSuggestProductPrice = ({
  className,
  variant,
  ...props
}: SearchSuggestProductPriceProps) => (
  <span
    data-slot="search-suggest-product-price"
    className={clsxm(searchSuggestProductPriceVariants({ variant }), className)}
    {...props}
  />
);
